
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { profileInteractionService } from "@/services/profileInteractionService";
import { matchService } from "@/services/matchService";
import { rateLimiter, RATE_LIMITS, createRateLimitKey } from "@/utils/rateLimiting";

interface User {
  id: string;
  user_id: string;
  display_name: string;
}

export const useProfileActions = (user: User, onClose: () => void) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [isBuzzing, setIsBuzzing] = useState(false);

  const handleBuzzz = async () => {
    if (!currentUser || isBuzzing) return;
    
    // Rate limiting check
    const rateLimitKey = createRateLimitKey(currentUser.id, 'buzzz');
    if (!rateLimiter.isAllowed(rateLimitKey, RATE_LIMITS.LIKE_SEND)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(rateLimitKey, RATE_LIMITS.LIKE_SEND) / 1000);
      toast({
        title: "Slow down!",
        description: `Please wait ${remainingTime} seconds before sending another buzzz`,
        variant: "default"
      });
      return;
    }
    
    setIsBuzzing(true);
    try {
      await profileInteractionService.sendBuzzz(currentUser.id, user.user_id);

      toast({
        title: "Buzzz sent! ⚡",
        description: `You sent a buzzz to ${user.display_name || 'this user'}`,
      });
      
    } catch (error: any) {
      console.error("Buzzz error details:", error);
      
      if (error?.message?.includes('duplicate') || error?.code === '23505') {
        toast({
          title: "Already sent!",
          description: "You can send a buzzz to a user once every 24 hours",
          variant: "default"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to send buzzz. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsBuzzing(false);
    }
  };

  const handleMessage = async () => {
    if (!currentUser) return;

    // Rate limiting check
    const rateLimitKey = createRateLimitKey(currentUser.id, 'message');
    if (!rateLimiter.isAllowed(rateLimitKey, RATE_LIMITS.MESSAGE_SEND)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(rateLimitKey, RATE_LIMITS.MESSAGE_SEND) / 1000);
      toast({
        title: "Slow down!",
        description: `Please wait ${remainingTime} seconds before starting another conversation`,
        variant: "default"
      });
      return;
    }

    try {
      // Create a direct conversation ID using sorted user IDs (no match check needed)
      const conversationId = matchService.createConversationId(currentUser.id, user.user_id);
      
      console.log('Navigating to conversation:', conversationId);
      
      // Close the modal first
      onClose();
      
      // Navigate using React Router
      navigate(`/messages/${conversationId}`);
      
      toast({
        title: "Opening conversation",
        description: `Starting chat with ${user.display_name || 'this user'}`,
      });
    } catch (error) {
      console.error('Navigation error:', error);
      toast({
        title: "Error",
        description: "Failed to start conversation",
        variant: "destructive"
      });
    }
  };

  const handleReport = () => {
    toast({
      title: "Report user",
      description: "Report functionality would be implemented here",
    });
  };

  return {
    handleBuzzz,
    handleMessage,
    handleReport,
    isBuzzing
  };
};
